import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { Accessibility, X, Contrast, Type, Link as LinkIcon, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

type TextSize = "base" | "lg" | "xl" | "xxl";

const STORAGE_KEY = "amr3-a11y";

type Prefs = {
  highContrast: boolean;
  highlightLinks: boolean;
  textSize: TextSize;
};

const DEFAULTS: Prefs = {
  highContrast: false,
  highlightLinks: false,
  textSize: "base",
};

function applyToHtml(prefs: Prefs) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.classList.toggle("a11y-high-contrast", prefs.highContrast);
  html.classList.toggle("a11y-highlight-links", prefs.highlightLinks);
  html.classList.remove("a11y-text-lg", "a11y-text-xl", "a11y-text-xxl");
  if (prefs.textSize === "lg") html.classList.add("a11y-text-lg");
  if (prefs.textSize === "xl") html.classList.add("a11y-text-xl");
  if (prefs.textSize === "xxl") html.classList.add("a11y-text-xxl");
}

type PanelContext = {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
};

const AccessibilityPanelContext = createContext<PanelContext | null>(null);

export function AccessibilityPanelProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  return (
    <AccessibilityPanelContext.Provider value={{ open, setOpen, toggle }}>
      {children}
    </AccessibilityPanelContext.Provider>
  );
}

export function useAccessibilityPanel() {
  const ctx = useContext(AccessibilityPanelContext);
  if (!ctx) throw new Error("useAccessibilityPanel must be used within AccessibilityPanelProvider");
  return ctx;
}

export function AccessibilityWidget() {
  const { t } = useI18n();
  const { open, setOpen, toggle } = useAccessibilityPanel();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);

  // Load saved prefs once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Prefs>) };
        setPrefs(parsed);
        applyToHtml(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Persist and apply on every change
  useEffect(() => {
    applyToHtml(prefs);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const update = <K extends keyof Prefs>(k: K, v: Prefs[K]) =>
    setPrefs((p) => ({ ...p, [k]: v }));

  const reset = () => setPrefs(DEFAULTS);

  const sizes: { id: TextSize; label: string; sample: string }[] = [
    { id: "base", label: t("a11y.size.base"), sample: "A" },
    { id: "lg", label: t("a11y.size.lg"), sample: "A+" },
    { id: "xl", label: t("a11y.size.xl"), sample: "A++" },
    { id: "xxl", label: t("a11y.size.xxl"), sample: "A+++" },
  ];

  return (
    <>
      {/* Floating trigger — desktop only */}
      <button
        type="button"
        onClick={toggle}
        aria-label={open ? t("a11y.close") : t("a11y.open")}
        aria-expanded={open}
        aria-controls="a11y-panel"
        data-a11y-ignore
        className={cn(
          "a11y-trigger fixed bottom-5 left-5 z-[70] hidden h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-xl ring-2 ring-white/80 transition-[transform,bottom] hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/40",
          "sm:bottom-6 sm:left-6 lg:inline-flex",
        )}
      >
        <Accessibility className="h-7 w-7" aria-hidden />
      </button>

      {open && (
        <>
          {/* Backdrop on mobile */}
          <button
            type="button"
            aria-label={t("a11y.close")}
            data-a11y-ignore
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[69] bg-black/30 sm:hidden"
          />
          <div
            id="a11y-panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="a11y-title"
            className={cn(
              "a11y-panel fixed z-[70] w-[calc(100vw-2rem)] max-w-sm overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",
              "bottom-24 left-5 sm:bottom-28 sm:left-6",
              "lg:bottom-28 lg:left-6",
              "max-sm:bottom-auto max-sm:left-1/2 max-sm:top-1/2 max-sm:-translate-x-1/2 max-sm:-translate-y-1/2",
            )}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 border-b border-border bg-gradient-to-br from-accent/10 via-card to-card px-5 py-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-sm">
                  <Accessibility className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <h2 id="a11y-title" className="font-serif text-lg font-bold leading-tight text-primary">
                    {t("a11y.title")}
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t("a11y.subtitle")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("a11y.close")}
                data-a11y-ignore
                className="-mr-1 -mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 px-5 py-5">
              {/* Toggles */}
              <div className="space-y-2.5">
                <Toggle
                  icon={<Contrast className="h-4 w-4" />}
                  label={t("a11y.contrast.label")}
                  description={t("a11y.contrast.desc")}
                  checked={prefs.highContrast}
                  onChange={(v) => update("highContrast", v)}
                />
                <Toggle
                  icon={<LinkIcon className="h-4 w-4" />}
                  label={t("a11y.links.label")}
                  description={t("a11y.links.desc")}
                  checked={prefs.highlightLinks}
                  onChange={(v) => update("highlightLinks", v)}
                />
              </div>

              {/* Text size */}
              <div className="rounded-xl border border-border bg-background p-3.5">
                <div className="mb-2.5 flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-accent/15 text-accent">
                    <Type className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-semibold text-primary">
                    {t("a11y.textSize")}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {sizes.map((s) => {
                    const active = prefs.textSize === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => update("textSize", s.id)}
                        aria-pressed={active}
                        aria-label={t("a11y.size.aria", { label: s.label })}
                        title={s.label}
                        data-a11y-ignore
                        className={cn(
                          "flex h-14 flex-col items-center justify-center rounded-md border px-1 transition-colors",
                          active
                            ? "border-accent bg-accent text-accent-foreground shadow-sm"
                            : "border-border bg-card text-primary hover:border-accent hover:bg-accent/5",
                        )}
                      >
                        <span className="font-serif text-base font-bold leading-none">
                          {s.sample}
                        </span>
                        <span className="mt-1 max-w-full truncate text-[9px] font-semibold uppercase tracking-wide opacity-80">
                          {s.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border bg-secondary/40 px-5 py-3">
              <button
                type="button"
                onClick={reset}
                data-a11y-ignore
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-accent"
              >
                <RotateCcw className="h-3.5 w-3.5" /> {t("a11y.reset")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Toggle({
  icon,
  label,
  description,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-a11y-ignore
      className="flex w-full items-start gap-3 rounded-md border border-border bg-background p-3 text-left transition-colors hover:border-accent"
    >
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
        {icon}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-semibold text-primary">{label}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
      <span
        className={cn(
          "mt-1 inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors",
          checked ? "bg-accent" : "bg-muted",
        )}
        aria-hidden
      >
        <span
          className={cn(
            "h-4 w-4 rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-4" : "translate-x-0",
          )}
        />
      </span>
    </button>
  );
}
